import {
	AlertCircleIcon,
	Tick02Icon,
	Time04Icon,
	UserMultipleIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head } from "@inertiajs/react";
import Dashboard from "@/components/dashboard";
import { ClientTaskItem } from "@/components/dashboard/client-task-item";
import { PersonTaskItem } from "@/components/dashboard/person-task-item";
import { VerificationTaskItem } from "@/components/dashboard/verification-task-item";
import { NewClientButton } from "@/components/new-client-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type HomeDashboard from "@/types/serializers/HomeDashboard";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	dashboard: HomeDashboard;
}

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleDateString("cs-CZ");
}

function formatExpiredAgo(daysUntilExpiry: number): string {
	const daysAgo = Math.abs(daysUntilExpiry);
	if (daysAgo === 0) return "dnes";
	if (daysAgo === 1) return "před 1 dnem";
	return `před ${daysAgo} dny`;
}

function VerificationCard({
	verified,
	total,
}: {
	verified: number;
	total: number;
}) {
	const percentage = total > 0 ? Math.round((verified / total) * 100) : 0;

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="flex items-center gap-2 font-medium text-sm">
					<HugeiconsIcon icon={Tick02Icon} strokeWidth={2} className="size-4" />
					Identifikace
				</CardTitle>
			</CardHeader>
			<CardContent className="flex items-center justify-between">
				<div>
					<div className="font-bold text-3xl">
						{verified} / {total}
					</div>
					<p className="text-muted-foreground text-sm">plně ověřeno</p>
				</div>
				<div className="font-bold text-3xl text-primary">{percentage}%</div>
			</CardContent>
		</Card>
	);
}

export default function Home({ user, dashboard }: Props) {
	const hasPersonMissingInfo = dashboard.person_missing_info_tasks.length > 0;
	const hasClientMissingInfo = dashboard.client_missing_info_tasks.length > 0;
	const hasDocumentExpired = dashboard.document_expired_tasks.length > 0;
	const hasVerificationExpired =
		dashboard.verification_expired_tasks.length > 0;
	const hasDocumentExpiring = dashboard.document_expiring_tasks.length > 0;
	const hasVerificationExpiring =
		dashboard.verification_expiring_tasks.length > 0;

	const hasTasks =
		hasPersonMissingInfo ||
		hasClientMissingInfo ||
		hasDocumentExpired ||
		hasVerificationExpired ||
		hasDocumentExpiring ||
		hasVerificationExpiring;

	return (
		<>
			<Head title="Dashboard" />
			<Dashboard user={user}>
				<div className="flex flex-1 flex-col gap-6 overflow-hidden p-4 lg:p-6">
					<div className="flex items-center justify-between">
						<h2 className="font-semibold text-2xl">
							Vítejte, {user.company_name}
						</h2>
						<NewClientButton />
					</div>

					<div className="grid grid-cols-2 gap-4">
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="flex items-center gap-2 font-medium text-sm">
									<HugeiconsIcon
										icon={UserMultipleIcon}
										strokeWidth={2}
										className="size-4"
									/>
									Klienti
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="font-bold text-3xl">
									{dashboard.total_clients}
								</div>
								<p className="text-muted-foreground text-sm">celkem</p>
							</CardContent>
						</Card>
						<VerificationCard
							verified={dashboard.verified_clients}
							total={dashboard.total_clients}
						/>
					</div>

					{hasTasks && (
						<Card className="flex-1 overflow-hidden">
							<CardHeader>
								<CardTitle>Úkoly</CardTitle>
							</CardHeader>
							<CardContent className="space-y-6 overflow-y-auto">
								{hasDocumentExpired && (
									<div className="space-y-3">
										<div className="flex items-center gap-2 font-medium text-sm">
											<HugeiconsIcon
												icon={AlertCircleIcon}
												strokeWidth={2}
												className="size-4 text-destructive"
											/>
											Prošlé doklady ({dashboard.document_expired_tasks.length})
										</div>
										<div className="space-y-2">
											{dashboard.document_expired_tasks.map(
												(task) =>
													task.expiry_date !== null &&
													task.days_until_expiry !== null && (
														<PersonTaskItem
															key={task.person_id}
															task={task}
															description={`doklad prošel ${formatExpiredAgo(task.days_until_expiry)}`}
															isExpired
														/>
													),
											)}
										</div>
									</div>
								)}

								{hasVerificationExpired && (
									<div className="space-y-3">
										<div className="flex items-center gap-2 font-medium text-sm">
											<HugeiconsIcon
												icon={AlertCircleIcon}
												strokeWidth={2}
												className="size-4 text-destructive"
											/>
											Prošlá identifikace (
											{dashboard.verification_expired_tasks.length})
										</div>
										<div className="space-y-2">
											{dashboard.verification_expired_tasks.map((task) => (
												<VerificationTaskItem
													key={`${task.person_id}-${task.client_id}`}
													task={task}
													isExpired
												/>
											))}
										</div>
									</div>
								)}

								{hasPersonMissingInfo && (
									<div className="space-y-3">
										<div className="flex items-center gap-2 font-medium text-sm">
											<HugeiconsIcon
												icon={AlertCircleIcon}
												strokeWidth={2}
												className="size-4 text-warning"
											/>
											Chybějící údaje osob (
											{dashboard.person_missing_info_tasks.length})
										</div>
										<div className="space-y-2">
											{dashboard.person_missing_info_tasks.map((task) => (
												<PersonTaskItem
													key={task.person_id}
													task={task}
													description={`Chybí: ${task.missing_items.join(", ")}`}
												/>
											))}
										</div>
									</div>
								)}

								{hasClientMissingInfo && (
									<div className="space-y-3">
										<div className="flex items-center gap-2 font-medium text-sm">
											<HugeiconsIcon
												icon={AlertCircleIcon}
												strokeWidth={2}
												className="size-4 text-warning"
											/>
											Chybějící údaje klientů (
											{dashboard.client_missing_info_tasks.length})
										</div>
										<div className="space-y-2">
											{dashboard.client_missing_info_tasks.map((task) => (
												<ClientTaskItem key={task.client_id} task={task} />
											))}
										</div>
									</div>
								)}

								{hasDocumentExpiring && (
									<div className="space-y-3">
										<div className="flex items-center gap-2 font-medium text-sm">
											<HugeiconsIcon
												icon={Time04Icon}
												strokeWidth={2}
												className="size-4 text-warning"
											/>
											Expirující doklady (
											{dashboard.document_expiring_tasks.length})
										</div>
										<div className="space-y-2">
											{dashboard.document_expiring_tasks.map(
												(task) =>
													task.expiry_date && (
														<PersonTaskItem
															key={task.person_id}
															task={task}
															description={`doklad expiruje ${formatDate(task.expiry_date)}`}
														/>
													),
											)}
										</div>
									</div>
								)}

								{hasVerificationExpiring && (
									<div className="space-y-3">
										<div className="flex items-center gap-2 font-medium text-sm">
											<HugeiconsIcon
												icon={Time04Icon}
												strokeWidth={2}
												className="size-4 text-warning"
											/>
											Expirující identifikace (
											{dashboard.verification_expiring_tasks.length})
										</div>
										<div className="space-y-2">
											{dashboard.verification_expiring_tasks.map((task) => (
												<VerificationTaskItem
													key={`${task.person_id}-${task.client_id}`}
													task={task}
												/>
											))}
										</div>
									</div>
								)}
							</CardContent>
						</Card>
					)}
				</div>
			</Dashboard>
		</>
	);
}
